import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import subscriptionService from '../services/subscriptionService';
import toast from 'react-hot-toast';
import {
  CreditCardIcon,
  CalendarIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  PauseIcon,
  PlayIcon,
  ArrowUpIcon,
  CogIcon
} from '@heroicons/react/24/outline';

interface UsageData {
  usageType: string;
  current: number;
  limit: number;
  percentage: number;
}

const ManageSubscriptionPage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<any>(null);
  const [plan, setPlan] = useState<any>(null);
  const [usage, setUsage] = useState<any>({});
  const [usageHistory, setUsageHistory] = useState<any[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    fetchSubscriptionData();
  }, [currentUser, navigate]);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const [subResponse, usageResponse, paymentResponse, historyResponse] = await Promise.all([
        subscriptionService.getCurrentSubscription(),
        subscriptionService.getUsageHistory(3),
        subscriptionService.getPaymentMethods(),
        subscriptionService.getUsageHistory(6)
      ]);

      if (subResponse.success && subResponse.data) {
        setSubscription(subResponse.data.subscription);
        setPlan(subResponse.data.plan);
        setUsage(subResponse.data.usage);
      }

      if (usageResponse.success && usageResponse.data) {
        setUsageHistory(usageResponse.data.usageHistory);
      }

      if (paymentResponse.success && paymentResponse.data) {
        setPaymentMethods(paymentResponse.data.paymentMethods);
      }

      if (historyResponse.success && historyResponse.data) {
        setUsageHistory(historyResponse.data.usageHistory);
      }
    } catch (error: any) {
      console.error('Failed to fetch subscription data:', error);
      toast.error('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async (immediate: boolean = false) => {
    try {
      setActionLoading('cancel');
      const response = await subscriptionService.cancelSubscription(immediate);
      
      if (response.success) {
        toast.success(immediate ? 'Subscription canceled immediately' : 'Subscription will be canceled at the end of your billing period');
        setShowCancelModal(false);
        fetchSubscriptionData();
      } else {
        throw new Error(response.message || 'Failed to cancel subscription');
      }
    } catch (error: any) {
      console.error('Cancel subscription error:', error);
      toast.error('Failed to cancel subscription: ' + (error.message || 'Unknown error'));
    } finally {
      setActionLoading(null);
    }
  };

  const handlePauseSubscription = async () => {
    try {
      setActionLoading('pause');
      const response = await subscriptionService.pauseSubscription();
      
      if (response.success) {
        toast.success('Subscription paused successfully');
        fetchSubscriptionData();
      } else {
        throw new Error(response.message || 'Failed to pause subscription');
      }
    } catch (error: any) {
      console.error('Pause subscription error:', error);
      toast.error('Failed to pause subscription: ' + (error.message || 'Unknown error'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleResumeSubscription = async () => {
    try {
      setActionLoading('resume');
      const response = await subscriptionService.resumeSubscription();
      
      if (response.success) {
        toast.success('Subscription resumed successfully');
        fetchSubscriptionData();
      } else {
        throw new Error(response.message || 'Failed to resume subscription');
      }
    } catch (error: any) {
      console.error('Resume subscription error:', error);
      toast.error('Failed to resume subscription: ' + (error.message || 'Unknown error'));
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="h-3 w-3 mr-1" />
            Active
          </span>
        );
      case 'canceled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="h-3 w-3 mr-1" />
            Canceled
          </span>
        );
      case 'paused':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <PauseIcon className="h-3 w-3 mr-1" />
            Paused
          </span>
        );
      case 'past_due':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
            Past Due
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  const getUserTypeSpecificUsage = () => {
    const userType = userProfile?.userType || 'student';
    
    if (userType === 'student') {
      return [
        { key: 'jobApplicationsSent', label: 'Job Applications' },
        { key: 'skillAssessmentsTaken', label: 'Skill Assessments' },
        { key: 'cvGenerations', label: 'CV Generations' },
        { key: 'coursesCompleted', label: 'Courses Completed' }
      ];
    } else {
      return [
        { key: 'jobPostingsCreated', label: 'Job Postings' },
        { key: 'candidateViews', label: 'Candidate Views' },
        { key: 'messagesSent', label: 'Messages Sent' },
        { key: 'backgroundChecksRun', label: 'Background Checks' }
      ];
    }
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!subscription && !plan) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h1 className="mt-4 text-xl font-semibold text-gray-900">No Active Subscription</h1>
          <p className="mt-2 text-gray-600">You don't have an active subscription. Choose a plan to get started.</p>
          <button
            onClick={() => navigate('/subscription')}
            className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            View Plans
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Manage Subscription</h1>
          <p className="mt-2 text-gray-600">View and manage your subscription, usage, and billing information</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Current Plan */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Current Plan</h2>
                {subscription && getStatusBadge(subscription.status)}
              </div>

              {plan && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-4">{plan.description}</p>
                    <div className="flex items-baseline">
                      <span className="text-2xl font-bold text-gray-900">
                        {subscriptionService.formatCurrency(
                          subscription?.billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice
                        )}
                      </span>
                      <span className="ml-1 text-gray-600">
                        /{subscription?.billingCycle || 'month'}
                      </span>
                    </div>
                  </div>

                  {subscription && (
                    <div className="space-y-3">
                      <div className="flex items-center text-sm text-gray-600">
                        <CalendarIcon className="h-4 w-4 mr-2" />
                        Started: {formatDate(subscription.currentPeriodStart)}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <CalendarIcon className="h-4 w-4 mr-2" />
                        {subscription.status === 'active' ? 'Renews' : 'Ends'}: {formatDate(subscription.currentPeriodEnd)}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <CreditCardIcon className="h-4 w-4 mr-2" />
                        Payment: {subscription.paymentMethod || 'Not set'}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => navigate('/subscription')}
                  className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <ArrowUpIcon className="h-4 w-4 mr-2" />
                  Upgrade Plan
                </button>

                {subscription?.status === 'active' && (
                  <>
                    <button
                      onClick={handlePauseSubscription}
                      disabled={actionLoading === 'pause'}
                      className="flex items-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50"
                    >
                      <PauseIcon className="h-4 w-4 mr-2" />
                      {actionLoading === 'pause' ? 'Pausing...' : 'Pause'}
                    </button>
                    <button
                      onClick={() => setShowCancelModal(true)}
                      className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <XCircleIcon className="h-4 w-4 mr-2" />
                      Cancel
                    </button>
                  </>
                )}

                {subscription?.status === 'paused' && (
                  <button
                    onClick={handleResumeSubscription}
                    disabled={actionLoading === 'resume'}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    <PlayIcon className="h-4 w-4 mr-2" />
                    {actionLoading === 'resume' ? 'Resuming...' : 'Resume'}
                  </button>
                )}
              </div>
            </div>

            {/* Usage This Month */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Usage This Month</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {getUserTypeSpecificUsage().map(({ key, label }) => {
                  const current = usage?.[key] || 0;
                  const limit = plan?.limits?.[key] || 0;
                  const percentage = subscriptionService.getUsagePercentage(current, limit);
                  
                  return (
                    <div key={key} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">{label}</span>
                        <span className="text-sm text-gray-600">
                          {current} / {subscriptionService.formatLimit(limit)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            percentage >= 90 ? 'bg-red-500' : 
                            percentage >= 75 ? 'bg-yellow-500' : 
                            'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(percentage, 100)}%` }}
                        />
                      </div>
                      {percentage >= 90 && (
                        <p className="text-xs text-red-600">
                          ⚠️ Approaching limit
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Usage History */}
            {usageHistory && usageHistory.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Usage History</h2>
                
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Month
                        </th>
                        {getUserTypeSpecificUsage().map(({ key, label }) => (
                          <th key={key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(usageHistory || []).slice(0, 6).map((monthData: any) => (
                        <tr key={monthData.month}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {new Date(monthData.month + '-01').toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long' 
                            })}
                          </td>
                          {getUserTypeSpecificUsage().map(({ key }) => (
                            <td key={key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {monthData.usage?.[key] || 0}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/subscription')}
                  className="w-full flex items-center px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <ArrowUpIcon className="h-4 w-4 mr-3 text-gray-400" />
                  View All Plans
                </button>
                <button
                  onClick={() => {/* TODO: Implement billing history */}}
                  className="w-full flex items-center px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <CreditCardIcon className="h-4 w-4 mr-3 text-gray-400" />
                  Billing History
                </button>
                <button
                  onClick={() => {/* TODO: Implement payment methods */}}
                  className="w-full flex items-center px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <CogIcon className="h-4 w-4 mr-3 text-gray-400" />
                  Payment Methods
                </button>
              </div>
            </div>

            {/* Plan Features */}
            {plan && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Plan Features</h3>
                <div className="space-y-2">
                  {Object.entries(plan.features || {})
                    .filter(([_, value]) => value === true)
                    .slice(0, 8)
                    .map(([feature, _]) => (
                      <div key={feature} className="flex items-center text-sm text-gray-600">
                        <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        {subscriptionService.getFeatureDisplayName(feature)}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Support */}
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Need Help?</h3>
              <p className="text-sm text-blue-700 mb-4">
                Have questions about your subscription or need assistance?
              </p>
              <button
                onClick={() => navigate('/support')}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="mt-3 text-center">
                <h3 className="text-lg font-medium text-gray-900">Cancel Subscription</h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to cancel your subscription? You can choose to cancel immediately or at the end of your billing period.
                  </p>
                </div>
                <div className="flex flex-col gap-3 px-4 py-3">
                  <button
                    onClick={() => handleCancelSubscription(false)}
                    disabled={actionLoading === 'cancel'}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {actionLoading === 'cancel' ? 'Canceling...' : 'Cancel at Period End'}
                  </button>
                  <button
                    onClick={() => handleCancelSubscription(true)}
                    disabled={actionLoading === 'cancel'}
                    className="bg-red-800 text-white px-4 py-2 rounded-lg hover:bg-red-900 transition-colors disabled:opacity-50"
                  >
                    Cancel Immediately
                  </button>
                  <button
                    onClick={() => setShowCancelModal(false)}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Keep Subscription
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSubscriptionPage;